import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gift, Crown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useDeadlineCountdown } from "@/components/Countdown";

const HIDE_ON = ["/join", "/watch", "/subscribe", "/login", "/register", "/auth/callback", "/payment/callback", "/profiles"];

// Site-wide slim promo bar (bottom): advertises the 24h-free offer to guests
// and nudges trial users to pick a paid plan before their free access ends.
export const PromoBar = () => {
  const { t } = useTranslation();
  const loc = useLocation();
  const { user, paid, onTrial, accessUntil } = useAuth();
  const { fromDisplay, freeAccess } = useCurrency();
  const { text: timeLeft } = useDeadlineCountdown(onTrial ? accessUntil : null);
  const [dismissed, setDismissed] = useState(sessionStorage.getItem("ntv_promo_dismissed") === "1");

  const onRoute = HIDE_ON.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  const hidden = paid || dismissed || onRoute || (user && (!onTrial || freeAccess)) || user === null;

  // Reserve space so the fixed bar never covers the last row of content.
  useEffect(() => {
    document.body.style.paddingBottom = hidden ? "" : "56px";
    return () => { document.body.style.paddingBottom = ""; };
  }, [hidden]);

  if (hidden) return null;

  const close = () => { sessionStorage.setItem("ntv_promo_dismissed", "1"); setDismissed(true); };
  const trial = !!(user && onTrial);
  const price = fromDisplay || "R19.99";
  const tm = timeLeft || "…";

  return (
    <div data-testid="promo-bar"
      className="fixed bottom-0 inset-x-0 z-50 bg-gradient-to-r from-brand via-red-600 to-brand text-white shadow-[0_-4px_20px_rgba(0,0,0,0.4)] animate-fade-up">
      <div className="px-3 sm:px-8 lg:px-12 py-2 sm:py-2.5 flex items-center justify-center gap-2 sm:gap-4 text-center">
        <span className="hidden sm:grid place-items-center w-8 h-8 rounded-full bg-white/20 shrink-0">
          {trial ? <Crown size={16} /> : <Gift size={16} />}
        </span>
        <p className="flex-1 sm:flex-none text-[13px] sm:text-base font-semibold leading-snug min-w-0 max-sm:truncate">
          <span className="sm:hidden">{freeAccess ? t("free.barShort") : trial ? t("promo.trialBarShort", { time: tm }) : t("promo.barShort", { price })}</span>
          <span className="hidden sm:inline">{freeAccess ? t("free.bar") : trial ? t("promo.trialBar", { time: tm }) : t("banner.trial", { price })}</span>
        </p>
        <Link to={trial ? "/subscribe" : "/register"} data-testid="promo-bar-cta"
          className="shrink-0 inline-flex items-center gap-1.5 bg-white text-brand font-bold text-xs sm:text-sm px-3 sm:px-4 py-1.5 rounded-full hover:bg-zinc-100 transition-colors whitespace-nowrap">
          {freeAccess ? t("free.cta") : trial ? t("promo.pickPlan") : t("banner.cta")}
        </Link>
        <button data-testid="promo-bar-close" onClick={close} aria-label="Dismiss"
          className="shrink-0 w-8 h-8 grid place-items-center rounded-full hover:bg-white/20 transition-colors">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
